import React, { useEffect, useMemo, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Filter, Loader2, ShieldAlert, Search, Download, Users, FileText, Calendar,
} from "lucide-react";

const MODULE_OPTIONS = [
  "Budget Scanner", "Infrastructure Risk", "Vendor & Procurement",
  "Payroll Audit", "Grants Audit", "Invoice Audit", "CyberWater",
  "Public Transparency", "Traffic Intelligence", "Sports & Athlete",
  "Business Efficiency", "Marketing / Traction", "Custom Anomaly", "Custom Audit",
];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const Inner = () => {
  const [filters, setFilters] = useState({
    user_email: "", module: "", date_from: "", date_to: "",
  });
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      if (filters.user_email) qs.append("user_email", filters.user_email);
      if (filters.module)     qs.append("module", filters.module);
      if (filters.date_from)  qs.append("date_from", filters.date_from);
      if (filters.date_to)    qs.append("date_to", filters.date_to);
      const r = await api.get(`/admin/reports${qs.toString() ? "?" + qs.toString() : ""}`);
      setReports(r.data?.reports || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) setError("Access denied. Admin role required.");
      else setError(err?.response?.data?.detail || "Could not load admin reports");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clearFilters = () => {
    setFilters({ user_email: "", module: "", date_from: "", date_to: "" });
    setTimeout(load, 0);
  };

  const exportCsv = () => {
    const header = ["audit_id", "template_key", "organization_name", "created_by", "created_at", "findings_created", "row_count"];
    const lines = [header.join(",")];
    for (const r of reports) {
      lines.push(header.map(h => {
        const v = r[h] ?? "";
        const s = String(v).replace(/"/g, '""');
        return s.includes(",") || s.includes("\"") ? `"${s}"` : s;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `auditiq-admin-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("CSV exported");
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const r of reports) {
      const owner = r.created_by || r.uploaded_by || "unknown";
      out[owner] = out[owner] || [];
      out[owner].push(r);
    }
    return out;
  }, [reports]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Admin · Reports Oversight</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 text-cream">All User Reports</h1>
          <p className="text-sm text-cream-dim mt-1">
            View, filter, and export every audit & scan run by any user across all organizations.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass px-3 py-2 text-xs text-cream-dim">
            <span className="text-gold font-mono text-base">{total}</span> reports ·{" "}
            <span className="text-gold font-mono text-base">{Object.keys(grouped).length}</span> users
          </div>
          <Button onClick={exportCsv} disabled={!reports.length} className="btn-ghost-gold h-10 px-4"
                  data-testid="admin-reports-export-btn">
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="glass p-5" data-testid="admin-reports-filters">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3 flex items-center gap-2">
          <Filter size={12} /> Filters
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">User Email</label>
            <Input
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              placeholder="user@example.com"
              className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
              data-testid="admin-reports-filter-email"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Module</label>
            <Select value={filters.module || "__all"}
                    onValueChange={(v) => setFilters({ ...filters, module: v === "__all" ? "" : v })}>
              <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                             data-testid="admin-reports-filter-module">
                <SelectValue placeholder="All modules" />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream max-h-72">
                <SelectItem value="__all">All modules</SelectItem>
                {MODULE_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Date From</label>
            <Input type="date"
                   value={filters.date_from}
                   onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                   className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                   data-testid="admin-reports-filter-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Date To</label>
            <Input type="date"
                   value={filters.date_to}
                   onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                   className="mt-1 bg-navy-deep border-gold-dim/40 text-cream"
                   data-testid="admin-reports-filter-to" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={load} className="btn-gold h-10 px-5" data-testid="admin-reports-apply-btn">
            <Search size={14} className="mr-2" /> Apply Filters
          </Button>
          <Button onClick={clearFilters} className="btn-ghost-gold h-10 px-4" data-testid="admin-reports-clear-btn">
            Clear
          </Button>
        </div>
      </div>

      {loading && (
        <div className="glass p-8 text-center text-cream-dim" data-testid="admin-reports-loading">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading reports…
        </div>
      )}

      {error && !loading && (
        <div className="glass p-8 text-center" data-testid="admin-reports-error">
          <ShieldAlert size={20} className="inline mr-2 text-[color:var(--auditiq-critical)]" />
          <span className="text-cream-dim">{error}</span>
        </div>
      )}

      {!loading && !error && reports.length === 0 && (
        <div className="glass p-10 text-center text-cream-dim" data-testid="admin-reports-empty">
          No reports match your filters.
        </div>
      )}

      {!loading && !error && Object.entries(grouped).map(([owner, rows]) => (
        <div key={owner} className="glass p-0 overflow-x-auto"
             data-testid={`admin-reports-user-${owner.replace(/[@.]/g, "-")}`}>
          <div className="px-5 py-3 border-b border-gold-dim/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-gold" />
              <h2 className="font-display text-base text-cream">{owner}</h2>
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] text-cream-dim">
              {rows.length} reports
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
              <tr className="border-b border-gold-dim/20">
                <th className="text-left p-3"><Calendar size={11} className="inline mr-1" />Date</th>
                <th className="text-left p-3">Organization</th>
                <th className="text-left p-3">Template</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Rows</th>
                <th className="text-left p-3">Findings</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.audit_id} className="border-b border-gold-dim/10" data-testid={`admin-report-row-${r.audit_id}`}>
                  <td className="p-3 text-cream-dim text-xs whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="p-3 text-cream">{r.organization_name || "—"}</td>
                  <td className="p-3 text-cream-dim text-xs">{r.template_key || "—"}</td>
                  <td className="p-3 text-cream-dim text-xs font-mono truncate max-w-[240px]">
                    {r.source_file || "—"}
                  </td>
                  <td className="p-3 text-gold font-mono text-xs">{r.row_count ?? "—"}</td>
                  <td className="p-3 text-gold font-mono text-xs">{r.findings_created ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const AdminReports = () => <PortalLayout><Inner /></PortalLayout>;
export default AdminReports;
